"use client";

import {
  NavLink,
  ScrollArea,
  useMantineTheme,
  Stack,
  Divider,
  AppShell,
  Badge,
  Flex,
} from "@forestsync/ui";
import { spotlight } from "@mantine/spotlight";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { platformNavItems } from "@/lib/navigation";
import Preferences from "@/components/Preferences";
import { FeedbackButton } from "@/components/layout/FeedbackButton";

interface NavBarProps {
  close: () => void;
}

export function NavBar({ close }: NavBarProps) {
  const pathname = usePathname();
  const theme = useMantineTheme();
  const t = useTranslations("navigation");
  const commonT = useTranslations("common");

  return (
    <>
      <AppShell.Section grow component={ScrollArea}>
        <Stack gap="xs">
          {platformNavItems.map((item) => {
            const Icon = item.icon;
            const navLinkStyles = {
              root: {
                borderRadius: theme.radius.md,
                opacity: item.disabled ? 0.75 : 1,
              },
            };
            return (
              <Flex key={item.href} align="center">
                {item.disabled && (
                  <Badge
                    size="xs"
                    variant="gradient"
                    gradient={{ from: "timber", to: "forest" }}
                    c="var(--mantine-color-body)"
                  >
                    {commonT("soon")}
                  </Badge>
                )}
                {item.spotlightTrigger ? (
                  <NavLink
                    label={t(item.id)}
                    leftSection={<Icon size={20} />}
                    disabled={item.disabled}
                    onClick={() => { close(); spotlight.open(); }}
                    styles={navLinkStyles}
                    w="100%"
                  />
                ) : (
                  <NavLink
                    component={Link}
                    href={item.href}
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                    label={t(item.id)}
                    leftSection={<Icon size={20} />}
                    active={!item.external && pathname === item.href}
                    disabled={item.disabled}
                    onClick={close}
                    styles={navLinkStyles}
                    w={item.disabled ? "max-content" : "100%"}
                  />
                )}
              </Flex>
            );
          })}
        </Stack>
      </AppShell.Section>

      <AppShell.Section>
        <Divider mb="md" />
        <FeedbackButton variant="menuItem" />
        <Flex justify="end">
          <Preferences />
        </Flex>
      </AppShell.Section>
    </>
  );
}
